import gsap from "gsap";
import { _splitText } from "./_splitText";
import { _backgroundGradient } from "./_backgroundGradient";
import { _section } from "./_section";
import { _event } from "./_event";

type OurWorksSliderEvents = {
    [key: string]: (instance: _ourWorksSlider, event?: any) => void;
};

type OurWorksSliderProps = {
    target: HTMLElement;
    events?: OurWorksSliderEvents;
};

type theme = {
    timer: {
        counter: number | null;
        interval: number | null;
    };
    current: string | null;
    colors: string[] | null;
};

export class _ourWorksSlider {
    target: HTMLElement;
    el: {
        wrapper: HTMLElement | null;
        slides: NodeListOf<HTMLElement>;
    };
    background: _backgroundGradient | null;

    animating: boolean;
    currentSlide: number | null;
    theme: theme;

    events: OurWorksSliderEvents;
    listeners: { [key: string]: Array<(instance: _ourWorksSlider, event?: any) => void> };

    constructor(props: OurWorksSliderProps) {
        this.target = props.target ?? null;

        this.el = {
            wrapper: this.target.querySelector(".js-our-works-slider-wrapper"),
            slides: this.target.querySelectorAll(".js-js-our-works-slider-item"),
        };

        this.background = null;

        this.animating = false;
        this.currentSlide = null;
        this.theme = {
            timer: {
                counter: 0,
                interval: null,
            },
            current: null,
            colors: null,
        };

        this.events = props?.events ?? {};
        this.listeners = {};

        this.init();
    }

    init(): void {
        this.background = new _backgroundGradient({ target: this.target });

        this.update(0);

        this.resize();
        window.addEventListener("resize", () => this.resize());

        // for mobile swipe
        if (_section.isMobile()) {
            const _touchStart = {
                x: 0,
                y: 0,
            };
            const _touchEnd = {
                x: 0,
                y: 0,
            };

            const _update = () => {
                const _diffX = _touchEnd.x - _touchStart.x;
                const _diffY = _touchEnd.y - _touchStart.y;
                const _threshold = 10;

                if (Math.abs(_diffX) > Math.abs(_diffY) && Math.abs(_diffX) > _threshold) {
                    const _position = _diffX <= 0 ? "right" : "left";

                    let _slideTo = _position === "left" ? this.currentSlide! - 1 : this.currentSlide! + 1;

                    if (_slideTo < 0) _slideTo = this.el.slides.length - 1;
                    if (_slideTo >= this.el.slides.length) _slideTo = 0;

                    this.update(_slideTo);
                }
            };

            this.target.addEventListener("touchstart", (e: TouchEvent) => {
                _touchStart.x = e.changedTouches[0].clientX;
                _touchStart.y = e.changedTouches[0].clientY;
            });

            this.target.addEventListener("touchend", (e: TouchEvent) => {
                _touchEnd.x = e.changedTouches[0].clientX;
                _touchEnd.y = e.changedTouches[0].clientY;
                _update();
            });
        }

        this.emit("init");
    }

    update(slide: number): void {
        if (slide === this.currentSlide) return;
        if (this.animating) return;
        this.animating = true;

        const _currentSlide = this.currentSlide !== null ? this.el.slides[this.currentSlide] : null;
        if (_currentSlide) {
            _currentSlide.style.display = "none";
        }

        const _slide = this.el.slides[slide];
        _slide.classList.remove("hidden");
        _slide.style.display = "";
        _slide.style.display = getComputedStyle(_slide).display;

        const _main = _slide.querySelector(".js-our-works-slider-item-main");
        if (_main) {
            gsap.fromTo(_main, { opacity: 0, y: 10 }, { duration: 0.8, opacity: 1, y: 0 });

            const _elements: HTMLElement[] = [];
            _main.querySelectorAll("*").forEach((item) => {
                let _element: HTMLElement | null = null;

                if (item.childNodes.length === 1 && item.childNodes[0].nodeType === Node.TEXT_NODE) {
                    const text = item.textContent?.trim();
                    if (text) _element = item as HTMLElement;
                }

                if (item.tagName.toLowerCase() === "h3") _element = item as HTMLElement;
                if (item.tagName.toLowerCase() === "img") _element = item.parentNode as HTMLElement;

                if (_element) {
                    const _parent = _element.closest("a");
                    if (_parent) _element = _parent as HTMLElement;

                    if (_elements.length <= 0) _elements.push(_element.parentNode as HTMLElement);
                    _elements.push(_element);
                }
            });

            const _class = "js-our-works-slider-item-stagger-item";
            _elements.forEach((item) => {
                const _tagName = item.tagName.toLowerCase();

                if (_tagName === "h3") {
                    new _splitText({ target: item });

                    item.querySelectorAll(".word").forEach((wordEl) => wordEl.classList.add(_class));
                } else {
                    item.classList.add(_class);
                }
            });

            gsap.fromTo(
                _main.querySelectorAll(`.${_class}`),
                {
                    opacity: (index: number, target: HTMLElement) => {
                        return target.classList.contains("word") ? 1 : 0;
                    },
                    y: (index: number, target: HTMLElement) => {
                        return target.classList.contains("word") ? (target.parentNode as HTMLElement).offsetHeight : 20;
                    },
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    stagger: 0.05,
                    delay: 0.5,
                    onComplete: () => {
                        this.animating = false;
                    },
                },
            );
        }

        const _banner = _slide.querySelector<HTMLElement>(".js-our-works-slider-item-banner");
        if (_banner) {
            gsap.fromTo(_banner, { opacity: 0, x: 40 }, { duration: 0.8, opacity: 1, x: 0 });
        }

        // update theme
        const _theme = _slide.dataset.theme ?? "light";
        const _colors = _slide.dataset.colors ? _slide.dataset.colors.split(",") : [];
        this.updateTheme(_theme, _colors);

        this.currentSlide = slide;
        this.resize();

        _event.dispatch(window, "scroll");

        this.emit("update");
    }

    updateTheme(theme: string, colors: string[]) {
        if (this.theme.current) this.target.classList.remove(this.theme.current);
        this.target.classList.add(theme);

        if (this.background)
            this.background.update({
                color1: colors[0] || this.background.defaults?.color1,
                color2: colors[1] || this.background.defaults?.color2,
                color3: colors[2] || this.background.defaults?.color3,
                color4: colors[3] || this.background.defaults?.color4,
                color5: colors[4] || this.background.defaults?.color5,
            });

        this.theme.timer.counter = 0;
        this.target.classList.add("theme-transition");

        if (!this.theme.timer.interval)
            this.theme.timer.interval = setInterval(() => {
                this.theme.timer.counter++;

                if (this.theme.timer.counter <= 80) return;
                this.theme.timer.counter = 0;

                clearInterval(this.theme.timer.interval);
                this.theme.timer.interval = null;

                this.target.classList.remove("theme-transition");
            }, 10);

        this.theme.current = theme;
    }

    resize(): void {
        if (this.currentSlide !== null && this.el.wrapper) {
            this.el.wrapper.style.minHeight = `${this.el.slides[this.currentSlide].offsetHeight}px`;
        }
    }

    on(type: string, callback: (instance: _ourWorksSlider, event?: any) => void): void {
        if (!this.listeners[type]) this.listeners[type] = [];
        this.listeners[type].push(callback);
    }

    off(type: string, callback: (instance: _ourWorksSlider, event?: any) => void): void {
        if (!this.listeners[type]) return;
        this.listeners[type] = this.listeners[type].filter((cb) => cb !== callback);
    }

    emit(type: string, event?: any): void {
        if (this.events[type]) this.events[type](this, event);
        if (!this.listeners[type]) return;
        this.listeners[type].forEach((callback) => callback(this, event));
    }
}
